import {
  formatDate,
  getEndOfDay,
  getEndOfMonth,
  getStartOfDay,
  getStartOfMonth,
  isSameDay,
} from '@travelata-libs/utils';
import { type CalendarSystemModelValue, type DateTimestamp, type DayCell } from '../Calendar.types';
import { ISO_MONDAY, ISO_SUNDAY, MS_IN_DAY } from './constants';

export const buildDayCells = (
  dates: DateTimestamp[],
  templateDate: DateTimestamp,
  options: { minDate: Date | null; maxDate: Date | null; disabledFromDate?: Date | null },
): DayCell[] => {
  const { minDate, maxDate, disabledFromDate = null } = options;
  const template = new Date(templateDate);
  const templateMonth = template.getMonth();
  const templateYear = template.getFullYear();
  const minTs = minDate ? getStartOfDay(minDate.getTime()) : null;
  const maxTs = maxDate ? getEndOfDay(maxDate.getTime()) : null;
  const disabledFromTs = disabledFromDate ? getStartOfDay(disabledFromDate.getTime()) : null;

  return dates.map((ts) => {
    const date = new Date(ts);
    const isOtherMonth = date.getMonth() !== templateMonth || date.getFullYear() !== templateYear;
    const dayStart = getStartOfDay(ts);
    const isDisabled =
      (minTs !== null && dayStart < minTs) ||
      (maxTs !== null && dayStart > maxTs) ||
      (disabledFromTs !== null && dayStart >= disabledFromTs);
    return { ts, dayStart, label: String(date.getDate()), isOtherMonth, isDisabled };
  });
};

const isFlexibleDay = (
  dayStart: DateTimestamp,
  selectionStart: DateTimestamp | null,
  flexibleDays: number | null,
  isDisabled: boolean,
  range: boolean,
): boolean => {
  if (!flexibleDays || selectionStart === null || isDisabled || range) {
    return false;
  }
  const diffInDays = Math.round((dayStart - selectionStart) / MS_IN_DAY);
  return diffInDays !== 0 && Math.abs(diffInDays) <= flexibleDays;
};

export const buildClassLists = (
  cells: DayCell[],
  selection: CalendarSystemModelValue,
  options: { flexibleDays: number | null; range: boolean; double: boolean; today: DateTimestamp },
): Record<string, boolean>[] => {
  const { flexibleDays, range, double, today } = options;
  const startRaw = selection?.start ?? null;
  const endRaw = selection?.end ?? null;
  const startDayStart = startRaw === null ? null : getStartOfDay(startRaw);
  const endDayStart = endRaw === null ? null : getStartOfDay(endRaw);

  return cells.map((cell) => {
    const isStart = startDayStart !== null && startDayStart === cell.dayStart;
    const isEnd = endDayStart !== null && endDayStart === cell.dayStart;
    const isHighlighted = startRaw !== null && endRaw !== null && cell.dayStart >= startRaw && cell.dayStart <= endRaw;
    const isFlexible = isFlexibleDay(cell.dayStart, startDayStart, flexibleDays, cell.isDisabled, range);
    const isToday = today === cell.dayStart;
    const isInRange = range && isHighlighted;
    const isoWeekDay = Number(formatDate(cell.dayStart, 'i'));
    const isRowStart = isoWeekDay === ISO_MONDAY;
    const isRowEnd = isoWeekDay === ISO_SUNDAY;
    const isFirstMonthDay = isSameDay(cell.dayStart, getStartOfMonth(cell.dayStart));
    const isLastMonthDay = isSameDay(cell.dayStart, getEndOfMonth(cell.dayStart));
    const isMonthEdgeVisible = isInRange && double && !cell.isOtherMonth;

    return {
      'tl-calendar-day_disabled': cell.isDisabled,
      'tl-calendar-day_highlighted': isHighlighted,
      'tl-calendar-day_start': isStart,
      'tl-calendar-day_end': isEnd,
      'tl-calendar-day_other': cell.isOtherMonth && !double,
      'tl-calendar-day_hidden': cell.isOtherMonth && double,
      'tl-calendar-day_bordered': isToday && !isFlexible,
      'tl-calendar-day_flexible': isFlexible,
      'tl-calendar-day_in-range': isInRange,
      'tl-calendar-day_row-start': isInRange && isRowStart,
      'tl-calendar-day_row-end': isInRange && isRowEnd,
      'tl-calendar-day_fade-start': isMonthEdgeVisible && isFirstMonthDay && !isStart && !isRowStart,
      'tl-calendar-day_fade-end': isMonthEdgeVisible && isLastMonthDay && !isEnd && !isRowEnd,
    };
  });
};
